import React, { useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { TooltipProps } from '../types';
import { useAccessibility } from '../hooks/useAccessibility';

const Tooltip: React.FC<TooltipProps> = ({
  hotspot,
  isVisible,
  position,
  onClose
}) => {
  const tooltipRef = useRef<HTMLDivElement>(null);
  const { announce, setupFocusTrap, cleanupFocusTrap } = useAccessibility();

  useEffect(() => {
    if (isVisible && tooltipRef.current) {
      setupFocusTrap(tooltipRef.current);
      announce(`Opened tooltip for ${hotspot.title}`);
    } else {
      cleanupFocusTrap();
    }

    return cleanupFocusTrap;
  }, [isVisible, hotspot.title, setupFocusTrap, cleanupFocusTrap, announce]);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isVisible) {
        onClose();
      }
    };

    if (isVisible) {
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isVisible, onClose]);

  const tooltipVariants = {
    hidden: {
      opacity: 0,
      scale: 0.8,
      y: 10,
    },
    visible: {
      opacity: 1,
      scale: 1,
      y: 0,
      transition: {
        type: 'spring',
        stiffness: 300,
        damping: 30,
      },
    },
    exit: {
      opacity: 0,
      scale: 0.8,
      y: 10,
      transition: {
        duration: 0.2,
      },
    },
  };

  // Calculate optimal position to keep tooltip fully visible
  const getOptimalPosition = () => {
    const tooltipWidth = 320; // max-width from CSS
    const tooltipHeight = 450; // increased estimated height
    const margin = 20;
    
    let left = position.x;
    let top = position.y - 20;
    
    // Adjust horizontal position if tooltip would go off screen
    if (left + tooltipWidth > window.innerWidth - margin) {
      left = window.innerWidth - tooltipWidth - margin;
    }
    if (left < margin) {
      left = margin;
    }
    
    // For lower hotspots, show tooltip above the hotspot
    if (position.y > window.innerHeight * 0.6) {
      top = position.y - tooltipHeight - 20; // Show above hotspot
    } else {
      top = position.y + 30; // Show below hotspot for upper ones
    }
    
    // Ensure tooltip doesn't go off screen vertically
    if (top < margin) {
      top = margin;
    }
    if (top + tooltipHeight > window.innerHeight - margin) {
      top = window.innerHeight - tooltipHeight - margin;
    }
    
    return { left, top };
  };

  const optimalPosition = getOptimalPosition();

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          ref={tooltipRef}
          className="tooltip fixed z-50"
          style={{
            left: `${optimalPosition.left}px`,
            top: `${optimalPosition.top}px`,
          }}
          variants={tooltipVariants}
          initial="hidden"
          animate="visible"
          exit="exit"
          role="dialog"
          aria-labelledby={`tooltip-title-${hotspot.id}`}
          aria-describedby={`tooltip-description-${hotspot.id}`}
        >
          <div className="flex justify-between items-start mb-3">
            <h3
              id={`tooltip-title-${hotspot.id}`}
              className="text-lg font-semibold text-gray-900"
            >
              {hotspot.title}
            </h3>
            <button
              onClick={(e) => {
                console.log('Close button clicked!');
                e.preventDefault();
                e.stopPropagation();
                onClose();
              }}
              onMouseDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
              className="text-gray-400 hover:text-gray-600 transition-colors p-1 rounded-full hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 cursor-pointer"
              aria-label="Close tooltip"
              type="button"
            >
              <XMarkIcon className="w-5 h-5" />
            </button>
          </div>

          <div
            id={`tooltip-description-${hotspot.id}`}
            className="space-y-3"
          >
            <p className="text-gray-700">{hotspot.description}</p>
            
            <div className="border-t pt-3">
              <div className="mb-4">
                <h4 className="font-medium text-red-700 mb-2 flex items-center">
                  <span className="w-2 h-2 bg-red-500 rounded-full mr-2"></span>
                  Problem:
                </h4>
                <p className="text-sm text-gray-700 bg-red-50 p-3 rounded-lg border border-red-200 break-words">
                  {hotspot.issue}
                </p>
              </div>
              
              <div className="mb-4">
                <h4 className="font-medium text-green-700 mb-2 flex items-center">
                  <span className="w-2 h-2 bg-green-500 rounded-full mr-2"></span>
                  Solution:
                </h4>
                <p className="text-sm text-gray-700 bg-green-50 p-3 rounded-lg border border-green-200 break-words">
                  {hotspot.solution}
                </p>
              </div>
            </div>

            <div className="flex gap-2 pt-2">
              {hotspot.productLink && (
                <button
                  className="btn-primary text-sm"
                  onClick={() => {
                    // Navigate to product page - now available in Milestone 3!
                    const productId = hotspot.productLink?.replace('/products/', '') || '';
                    window.location.href = `#products-${productId}`;
                    alert(`🎉 Product Pages Now Available!\n\nNavigate to the Products section to view detailed information about ${hotspot.title}.\n\nMilestone 3 is complete with comprehensive product guides, specifications, and step-by-step instructions!`);
                  }}
                >
                  View Product
                </button>
              )}
              {hotspot.videoLink && (
                <button
                  className="btn-secondary text-sm"
                  onClick={() => {
                    // Placeholder action - will open video in future milestones
                    alert(`🎥 Coming Soon!\n\nThis will open: ${hotspot.videoLink}\n\nIn Milestone 4, we'll integrate video tutorials and step-by-step guides for each household issue.`);
                  }}
                >
                  Watch Video
                </button>
              )}
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default Tooltip;
